// src/components/profile/CourseHistory.tsx
// Komponen riwayat kursus dengan timeline layout

import React from "react";
import { BookOpen, Clock, MapPin, User } from "lucide-react";
import { useOptimizedProfile } from "../../hooks/useOptimizedProfile";
import SpotlightCard from "../SpotlightCard/SpotlightCard";
import { PhotoProvider, PhotoView } from "react-photo-view";
import "react-photo-view/dist/react-photo-view.css";

const CourseHistory: React.FC = () => {
    const { profileData, loading, error } = useOptimizedProfile();
    const courseHistory = profileData?.courseHistory || [];

    // Fungsi untuk format periode kursus
    const formatPeriod = (course: any): string => {
        const startDate = new Date(course.start_date);
        const startYear = startDate.getFullYear();
        const startMonth = startDate.toLocaleDateString("id-ID", {
            month: "short",
        });

        if (course.is_current || !course.end_date) {
            return `${startMonth} ${startYear} - Sekarang`;
        }

        const endDate = new Date(course.end_date);
        const endYear = endDate.getFullYear();
        const endMonth = endDate.toLocaleDateString("id-ID", {
            month: "short",
        });

        if (startYear === endYear) {
            return `${startMonth} - ${endMonth} ${startYear}`;
        }

        return `${startMonth} ${startYear} - ${endMonth} ${endYear}`;
    };

    // Fungsi untuk mendapatkan warna berdasarkan kategori kursus
    const getCourseColor = (category: string) => {
        const normalizedCategory = category.toLowerCase();
        switch (normalizedCategory) {
            case "akademik":
            case "matematika":
            case "sains":
                return {
                    bg: "bg-blue-50",
                    border: "border-blue-200",
                    icon: "text-blue-600",
                    dot: "bg-blue-500",
                };
            case "bahasa":
            case "english":
            case "inggris":
                return {
                    bg: "bg-green-50",
                    border: "border-green-200",
                    icon: "text-green-600",
                    dot: "bg-green-500",
                };
            case "seni":
            case "musik":
            case "menggambar":
                return {
                    bg: "bg-purple-50",
                    border: "border-purple-200",
                    icon: "text-purple-600",
                    dot: "bg-purple-500",
                };
            case "olahraga":
            case "renang":
            case "sepak bola":
                return {
                    bg: "bg-orange-50",
                    border: "border-orange-200",
                    icon: "text-orange-600",
                    dot: "bg-orange-500",
                };
            case "teknologi":
            case "komputer":
            case "coding":
                return {
                    bg: "bg-indigo-50",
                    border: "border-indigo-200",
                    icon: "text-indigo-600",
                    dot: "bg-indigo-500",
                };
            default:
                return {
                    bg: "bg-gray-50",
                    border: "border-gray-200",
                    icon: "text-gray-600",
                    dot: "bg-gray-500",
                };
        }
    };



    if (loading) {
        return (
            <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8">
                <div className="flex items-center justify-between mb-8">
                    <div className="h-8 bg-gray-200 rounded w-48 animate-pulse"></div>
                    <div className="h-6 bg-gray-200 rounded w-24 animate-pulse"></div>
                </div>
                <div className="space-y-8">
                    {[1, 2, 3].map((i) => (
                        <div key={i} className="relative flex items-start">
                            <div className="w-16 h-16 bg-gray-200 rounded-full animate-pulse"></div>
                            <div className="ml-6 flex-1 p-6 bg-gray-50 rounded-xl">
                                <div className="h-6 bg-gray-200 rounded w-3/4 mb-3 animate-pulse"></div>
                                <div className="h-4 bg-gray-200 rounded w-1/2 mb-4 animate-pulse"></div>
                                <div className="h-4 bg-gray-200 rounded w-full animate-pulse"></div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-white rounded-2xl shadow-lg p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                    Kegiatan Kursus
                </h2>
                <div className="text-center py-12">
                    <BookOpen className="h-16 w-16 text-red-300 mx-auto mb-4" />
                    <p className="text-red-500 text-lg">
                        Error memuat data kursus: {error}
                    </p>
                </div>
            </div>
        );
    }

    if (!courseHistory || courseHistory.length === 0) {
        return (
            <div className="bg-white rounded-2xl shadow-lg p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                    Kegiatan Kursus
                </h2>
                <div className="text-center py-12">
                    <BookOpen className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500 text-lg">
                        Belum ada kegiatan kursus yang ditambahkan
                    </p>
                </div>
            </div>
        );
    }

    // Urutkan kursus berdasarkan tanggal mulai (terbaru dulu)
    const sortedCourses = [...courseHistory].sort((a, b) => {
        const dateA = new Date(a.start_date).getTime();
        const dateB = new Date(b.start_date).getTime();
        return dateB - dateA;
    });

    return (
        <PhotoProvider>
            <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-6 md:p-8">
                {/* Header */}
                <div className="flex items-center justify-between mb-6 sm:mb-8">
                    <h2 className="text-xl sm:text-2xl font-bold text-gray-900">
                        Kegiatan Kursus
                    </h2>
                    <div className="flex items-center text-xs sm:text-sm text-gray-500">
                        <BookOpen className="h-4 w-4 mr-1 text-green-500" />
                        <span>{courseHistory.length} kursus</span>
                    </div>
                </div>

                {/* Timeline */}
                <div className="relative">
                    {/* Timeline Line */}
                    <div className="absolute left-4 sm:left-7 top-2 bottom-2 w-0.5 bg-gradient-to-b from-blue-200 via-purple-200 to-green-200"></div>

                    {/* Course Items */}
                    <div className="space-y-4 sm:space-y-8">
                        {sortedCourses.map((course) => {
                            const colors = getCourseColor(course.category);

                            return (
                                <div
                                    key={course.id}
                                    className="relative flex items-start group"
                                >
                                    {/* Timeline Dot */}
                                    <div
                                        className={`
                      relative z-10 flex items-center justify-center w-8 h-8 sm:w-14 sm:h-14 rounded-full border-2 sm:border-4 border-white shadow-md sm:shadow-lg
                      transition-transform duration-300 group-hover:scale-110 flex-shrink-0
                      ${colors.dot} 
                    `}
                                    >
                                        <BookOpen
                                            className={`h-4 w-4 sm:h-6 sm:w-6 text-white`}
                                        />
                                    </div>

                                    {/* SpotlightCard - Course Info */}
                                    <div className="ml-3 sm:ml-6 flex-1 min-w-0">
                                        <SpotlightCard
                                            className="bg-white border border-gray-200 rounded-xl shadow-xs sm:shadow-md hover:shadow-lg transition-shadow duration-300"
                                            spotlightColor="rgba(59, 130, 246, 0.15)"
                                        >
                                            <div className="p-3.5 sm:p-6">
                                                <div className="flex flex-col-reverse sm:flex-row sm:items-start justify-between gap-3 sm:gap-4">
                                                    <div className="flex-1 min-w-0">
                                                        {/* Badges row */}
                                                        <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 mb-1.5 sm:mb-2">
                                                            <span
                                                                className={`inline-flex items-center px-2 sm:px-2.5 py-0.5 rounded-full text-xs font-semibold ${colors.icon} ${colors.bg} border ${colors.border}`}
                                                            >
                                                                {course.category}
                                                            </span>
                                                            <span
                                                                className={`inline-flex items-center px-2 sm:px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                                                    course.status === "ongoing"
                                                                        ? "bg-green-100 text-green-800"
                                                                        : course.status === "completed"
                                                                        ? "bg-gray-100 text-gray-800"
                                                                        : course.status === "paused"
                                                                        ? "bg-yellow-100 text-yellow-800"
                                                                        : "bg-red-100 text-red-800"
                                                                }`}
                                                            >
                                                                {course.status === "ongoing"
                                                                    ? "Sedang Berlangsung"
                                                                    : course.status === "completed"
                                                                    ? "Selesai"
                                                                    : course.status === "paused"
                                                                    ? "Ditunda"
                                                                    : "Dibatalkan"}
                                                            </span>
                                                            {course.subject && (
                                                                <span className="text-xs text-gray-500 font-medium">
                                                                    • {course.subject}
                                                                </span>
                                                            )}
                                                        </div>

                                                        {/* Course Name */}
                                                        <h3 className="text-base sm:text-xl font-bold text-gray-900 leading-snug break-words">
                                                            {course.course_name}
                                                        </h3>

                                                        {/* Period */}
                                                        <p className="text-xs sm:text-sm text-gray-600 font-medium mt-1">
                                                            {formatPeriod(course)}
                                                        </p>

                                                        {/* Course Metadata */}
                                                        <div className="text-xs sm:text-sm text-gray-600 mt-2 space-y-1">
                                                            <div className="flex items-center gap-1.5">
                                                                <MapPin className="h-3.5 w-3.5 sm:h-4 sm:w-4 flex-shrink-0 text-gray-400" />
                                                                <span className="truncate">{course.institution}</span>
                                                            </div>
                                                            {course.instructor && (
                                                                <div className="flex items-center gap-1.5">
                                                                    <User className="h-3.5 w-3.5 sm:h-4 sm:w-4 flex-shrink-0 text-gray-400" />
                                                                    <span className="truncate">{course.instructor}</span>
                                                                </div>
                                                            )}
                                                            <div className="flex items-center gap-1.5">
                                                                <Clock className="h-3.5 w-3.5 sm:h-4 sm:w-4 flex-shrink-0 text-gray-400" />
                                                                <span className="truncate">
                                                                    {new Date(course.start_date).toLocaleDateString("id-ID", {
                                                                        year: "numeric",
                                                                        month: "short",
                                                                        day: "numeric",
                                                                    })}
                                                                    {course.end_date
                                                                        ? ` - ${new Date(course.end_date).toLocaleDateString("id-ID", {
                                                                              year: "numeric",
                                                                              month: "short",
                                                                              day: "numeric",
                                                                          })}`
                                                                        : course.is_current && " - Sekarang"}
                                                                </span>
                                                            </div>
                                                            {course.schedule && (
                                                                <div className="flex items-center gap-1.5">
                                                                    <Clock className="h-3.5 w-3.5 sm:h-4 sm:w-4 flex-shrink-0 text-gray-400" />
                                                                    <span className="truncate">{course.schedule}</span>
                                                                </div>
                                                            )}
                                                        </div>

                                                        {/* Description */}
                                                        {course.description && (
                                                            <p className="text-xs sm:text-sm text-gray-500 mt-2 break-words leading-relaxed">
                                                                {course.description}
                                                            </p>
                                                        )}
                                                    </div>

                                                    {/* Thumbnail Image */}
                                                    {course.image_url && (
                                                        <div className="flex-shrink-0 self-start sm:self-center">
                                                            <PhotoView src={course.image_url}>
                                                                <img
                                                                    src={course.image_url}
                                                                    alt={`Logo ${course.institution}`}
                                                                    className="w-12 h-12 sm:w-16 sm:h-16 rounded-xl object-cover cursor-pointer hover:opacity-80 transition-opacity duration-200 border border-gray-200 shadow-xs"
                                                                />
                                                            </PhotoView>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </SpotlightCard>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </PhotoProvider>
    );
};

export default CourseHistory;
